/**
 * Global error reporting til backend.
 *
 * - Setter opp window.onerror og unhandledrejection-listener
 * - Eksponerer reportError() for ErrorBoundary
 * - Bruker navigator.sendBeacon når mulig (overlever page-unload)
 * - In-memory rate-limit for å hindre spam fra raske gjenoppståelser
 */

interface ErrorReport {
  message: string;
  stack?: string;
  url?: string;
  componentStack?: string;
  severity?: 'error' | 'warning';
  source?: string;
  extra?: Record<string, unknown>;
}

const recentSignatures = new Map<string, number>();
const SIGNATURE_RATE_MS = 60_000; // ikke send samme feil mer enn én gang per minutt fra én klient

function signatureFor(report: ErrorReport): string {
  const stackTop = (report.stack || '').split('\n').find(l => l.trim().startsWith('at '))?.trim() || '';
  return `${report.message.slice(0, 80)}|${stackTop.slice(0, 120)}`;
}

export function reportError(report: ErrorReport): void {
  try {
    const sig = signatureFor(report);
    const now = Date.now();
    const last = recentSignatures.get(sig) || 0;
    if (now - last < SIGNATURE_RATE_MS) return;
    recentSignatures.set(sig, now);

    const payload = JSON.stringify({
      message: report.message,
      stack: report.stack,
      url: report.url || window.location.href,
      componentStack: report.componentStack,
      severity: report.severity || 'error',
      source: report.source || 'frontend-kandidatportal',
      extra: {
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        ...report.extra
      }
    });

    // sendBeacon overlever page-unload (viktig hvis feilen skjer rett før navigasjon)
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon('/api/errors', blob);
      if (ok) return;
    }
    // Fallback til fetch (uten å vente på response)
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true
    }).catch(() => { /* svelg — error-reporting skal aldri bli kilde til feil */ });
  } catch {
    // svelg
  }
}

export function installGlobalErrorHandlers(): void {
  // Synkrone JS-feil
  window.addEventListener('error', (event) => {
    if (!event.message) return;
    reportError({
      message: event.message,
      stack: event.error?.stack,
      url: event.filename ? `${window.location.href} (script: ${event.filename}:${event.lineno})` : window.location.href,
      severity: 'error',
      source: 'window.onerror'
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    reportError({
      message: `Unhandled rejection: ${message}`,
      stack,
      severity: 'error',
      source: 'unhandledrejection'
    });
  });
}
